import React, { useState, useEffect, useCallback, useRef } from 'react';
import { T, API_BASE } from '../constants';

// Generic ES-Desk monitor — one component drives all six MANSA ES-desk systems
// (SIRIUS / RIGEL / MARS / SATURN / VENUS / ISINMI). Polls
// /strategies/api/strategies/<system>/decisions/ every 20s. The endpoint returns
// a uniform shape: { system, instrument, session, blurb, summary, decisions[] }
// where each decision carries a human `headline` + every gate input. Advisory/LOG,
// shadow (ES_PHASE). Pass `system` (lowercase slug) + an accent colour.

const REFRESH_MS = 20000;
const num = (v, d = 2) => (v == null ? '—' : Number(v).toFixed(d));

// Time-of-day in ET from an ISO ts. Offset-less timestamps (the synthetic EXIT
// row carries naive-UTC) are treated as UTC so they don't drift by the browser tz.
function fmtET(ts) {
  if (!ts) return '';
  const s = /([+-]\d\d:?\d\d|Z)$/.test(ts) ? ts : ts + 'Z';
  const d = new Date(s);
  return isNaN(d.getTime()) ? '' : d.toLocaleTimeString('en-GB', { timeZone: 'America/New_York', hour12: false });
}

const STATUS_COLORS = {
  OPEN: '#3b82f6', EXIT: '#3b82f6', STOP: T.bear,
  HOLD: T.bull, SKIP: T.textMuted, PAUSED: T.textMuted,
};
const GRADE_COLORS = { A: '#16a34a', B: '#2962ff', D: T.textMuted };

function Stat({ label, value, color }) {
  return (
    <div style={{ flex: '1 1 110px', padding: '12px 14px', borderRadius: 6,
      background: 'rgba(127,127,127,0.05)', border: `1px solid ${T.border}` }}>
      <div style={{ fontSize: 10, color: T.textMuted, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>{label}</div>
      <div style={{ fontSize: 22, color: color || T.text, fontFamily: 'Roboto Mono, monospace', marginTop: 4 }}>{value}</div>
    </div>
  );
}
function Pill({ value, color }) {
  if (value == null || value === '') return <span style={{ color: T.textMuted }}>—</span>;
  return (
    <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 4, fontSize: 10,
      fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase',
      background: `${color || T.border}22`, border: `1px solid ${color || T.border}66`, color: color || T.text }}>{String(value)}</span>
  );
}

export default function EsDeskMonitor({ system, accent = '#6366f1' }) {
  const [data, setData] = useState({ decisions: [], summary: {}, blurb: '', session: '', instrument: '', available: true });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastFetched, setLastFetched] = useState(null);
  const [actionFilter, setActionFilter] = useState('');
  const [limit, setLimit] = useState(100);
  const timerRef = useRef(null);

  const fetchRows = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      let url = `${API_BASE}/strategies/api/strategies/${system}/decisions/?limit=${limit}`;
      if (actionFilter) url += `&action=${actionFilter}`;
      const res = await fetch(url, { cache: 'no-store' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const j = await res.json();
      setData({
        decisions: j.decisions || [], summary: j.summary || {},
        blurb: j.blurb || '', session: j.session || '', instrument: j.instrument || '',
        available: j.available !== false,
      });
      setLastFetched(new Date());
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [system, limit, actionFilter]);

  useEffect(() => {
    fetchRows();
    timerRef.current = setInterval(fetchRows, REFRESH_MS);
    return () => clearInterval(timerRef.current);
  }, [fetchRows]);

  const { decisions, summary, blurb, session, instrument, available } = data;
  const name = (system || '').toUpperCase();

  return (
    <div style={{ padding: '20px 24px', maxWidth: 1440, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap', marginBottom: 4 }}>
        <h1 style={{ margin: 0, fontSize: 22, color: T.text }}>{name}</h1>
        {session && <Pill value={session} color={accent} />}
        <Pill value="SHADOW · 1c" color={T.textMuted} />
        {!available && <Pill value="awaiting feed" color={T.bear} />}
      </div>
      <div style={{ fontSize: 12, color: T.textMuted, marginBottom: 16, lineHeight: 1.55, maxWidth: 980 }}>{blurb}</div>

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
        <Stat label="Evals" value={summary.evals ?? 0} />
        <Stat label="Holds" value={summary.holds ?? 0} color={accent} />
        <Stat label="Skips" value={summary.skips ?? 0} color={T.textMuted} />
        <Stat label="Open" value={summary.open ?? 0} color="#3b82f6" />
        <Stat label="Stopped" value={summary.stopped ?? 0} color={T.bear} />
        <Stat label="Net bp (hold)" value={num(summary.net_bp, 1)} color={(summary.net_bp || 0) >= 0 ? T.bull : T.bear} />
      </div>

      {decisions[0]?.gates?.length > 0 && (
        <div style={{ marginBottom: 16, border: `1px solid ${T.border}`, borderRadius: 8, padding: '12px 14px', background: 'rgba(127,127,127,0.04)' }}>
          <div style={{ fontSize: 11, color: T.textMuted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>
            Latest evaluation · {decisions[0].session_date} · gate breakdown
          </div>
          {decisions[0].gates.map((gt, i) => (
            <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'baseline', padding: '5px 0', borderTop: i ? `1px solid ${T.border}` : 'none', fontSize: 12 }}>
              <span style={{ flex: '1 1 auto', color: T.text }}>{gt.label}</span>
              <span style={{ flex: '0 0 auto', color: T.textMuted, fontFamily: 'Roboto Mono, monospace' }}>{gt.value}</span>
              <span style={{ flex: '0 0 64px', textAlign: 'right', fontWeight: 700, fontFamily: 'Roboto Mono, monospace',
                color: gt.result === '✓' ? T.bull : gt.result === '✗' ? T.bear : accent }}>{gt.result}</span>
            </div>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
        <select value={actionFilter} onChange={e => setActionFilter(e.target.value)}
          style={{ background: T.surface, color: T.text, border: `1px solid ${T.border}`, borderRadius: 6, padding: '6px 10px' }}>
          <option value="">All</option><option value="HOLD">HOLD</option><option value="SKIP">SKIP</option>
        </select>
        <select value={limit} onChange={e => setLimit(Number(e.target.value))}
          style={{ background: T.surface, color: T.text, border: `1px solid ${T.border}`, borderRadius: 6, padding: '6px 10px' }}>
          {[50, 100, 200].map(n => <option key={n} value={n}>{n} rows</option>)}
        </select>
        <button onClick={fetchRows} disabled={loading}
          style={{ background: accent, color: '#fff', border: 'none', borderRadius: 6, padding: '7px 16px', fontWeight: 700, cursor: 'pointer' }}>
          {loading ? '…' : 'Refresh'}</button>
        <span style={{ fontSize: 11, color: T.textMuted }}>
          {decisions.length} rows · {instrument} {lastFetched ? `· updated ${lastFetched.toLocaleTimeString('en-GB', { timeZone: 'America/New_York', hour12: false })} ET` : ''}
        </span>
      </div>

      {error && <div style={{ color: T.bear, marginBottom: 12 }}>Error: {error}</div>}

      <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: '60vh', border: `1px solid ${T.border}`, borderRadius: 8 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ background: 'rgba(127,127,127,0.06)', textAlign: 'left' }}>
              {['Session', 'Decision', 'Side', 'Grade', 'Mult', 'Entry', 'Stop', 'Exit', 'PnL bp', 'Status', 'Detail'].map(h => (
                <th key={h} style={{ padding: '10px 12px', color: T.textMuted, fontWeight: 700, whiteSpace: 'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {decisions.length === 0 && (
              <tr><td colSpan={11} style={{ padding: 24, textAlign: 'center', color: T.textMuted }}>No decisions yet — shadow feed warming up.</td></tr>
            )}
            {decisions.map((r, i) => (
              <tr key={`${r.session_date}-${i}`} style={{ borderTop: `1px solid ${T.border}` }}>
                <td style={{ padding: '9px 12px', fontFamily: 'Roboto Mono, monospace', whiteSpace: 'nowrap' }}>{r.session_date || '—'}{r.signal_ts ? <span style={{ color: T.textMuted }}>{' ' + fmtET(r.signal_ts)}</span> : ''}</td>
                <td style={{ padding: '9px 12px' }}><Pill value={r.decision} color={r.decision === 'HOLD' ? accent : T.textMuted} /></td>
                <td style={{ padding: '9px 12px' }}><Pill value={r.side} color={r.side === 'LONG' ? T.bull : r.side === 'SHORT' ? T.bear : T.textMuted} /></td>
                <td style={{ padding: '9px 12px' }}><Pill value={r.sizing_grade} color={GRADE_COLORS[r.sizing_grade] || T.textMuted} /></td>
                <td style={{ padding: '9px 12px', fontFamily: 'Roboto Mono, monospace', color: T.textMuted }}>{r.sizing_mult == null ? '—' : `${num(r.sizing_mult, 1)}×`}</td>
                <td style={{ padding: '9px 12px', fontFamily: 'Roboto Mono, monospace' }}>{num(r.entry_price)}</td>
                <td style={{ padding: '9px 12px', fontFamily: 'Roboto Mono, monospace', color: T.bear }}>{num(r.stop_price)}</td>
                <td style={{ padding: '9px 12px', fontFamily: 'Roboto Mono, monospace' }}>{num(r.exit_price)}</td>
                <td style={{ padding: '9px 12px', fontFamily: 'Roboto Mono, monospace', color: (r.pnl_bp || 0) >= 0 ? T.bull : T.bear }}>{r.pnl_bp == null ? '—' : num(r.pnl_bp, 1)}</td>
                <td style={{ padding: '9px 12px' }}><Pill value={r.status} color={STATUS_COLORS[r.status] || T.textMuted} /></td>
                <td style={{ padding: '9px 12px', color: T.textDim, maxWidth: 360, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.headline}>{r.headline}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
